import React, { useState, useEffect } from 'react';
import { ArrowUpRight, LayoutGrid, List, Monitor, Fingerprint, Calendar, Loader2, ArrowLeft, ImageOff } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

// 🟢 Interfaces
export interface BrandCollection {
  id: string;
  brandName?: string;
  collectionName?: string;
  title?: string; // Added for new database structure
  season?: string;
  pieceCount?: number;
  image?: string;
  imageUrl?: string; // Added for new database structure
  status?: 'ACTIVE' | 'ARCHIVED' | 'UPCOMING';
  isPrivate?: boolean;
}

export interface CollectionsViewProps {
  // 🟢 Made strictly required so the parent component MUST provide it
  onSelectProduct: (product: any) => void; 
}

// 🟢 HELPER: Bulletproof Image Fallback
const SafeImage = ({ src, alt, className, fallbackType = 'image' }: { src?: string, alt: string, className: string, fallbackType?: 'image' | 'solid' }) => {
    const [hasError, setHasError] = useState(false);
    
    // Aesthetic placeholder for the main collection backgrounds
    const fallbackImage = 'https://images.unsplash.com/photo-1552346154-21d32810baa3?auto=format&fit=crop&q=80&w=800';

    if (!src || hasError) {
        if (fallbackType === 'image') {
            return <img src={fallbackImage} alt="Fallback" className={className} />;
        }
        return (
            <div className={`bg-zinc-100 flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 ${className}`}>
                <ImageOff size={24} className="text-zinc-300 mb-2" />
                <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-widest text-center px-2">No Image<br/>Data</span>
            </div>
        );
    }

    return (
        <img 
            src={src} 
            alt={alt} 
            className={className}
            onError={() => setHasError(true)}
        />
    );
};

export const CollectionsView: React.FC<CollectionsViewProps> = ({ onSelectProduct }) => {
  const [collections, setCollections] = useState<BrandCollection[]>([]);
  const [loading, setLoading] = useState(true);

  // Detail View State
  const [selectedCollection, setSelectedCollection] = useState<BrandCollection | null>(null);
  const [collectionProducts, setCollectionProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // 🟢 1. FETCH ALL COLLECTIONS
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const collectionsRef = collection(db, 'collections');
        const snapshot = await getDocs(collectionsRef);
        
        const fetchedCollections = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as BrandCollection[];

        setCollections(fetchedCollections);
      } catch (error) {
        console.error("Error fetching collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  // 🟢 2. FETCH PRODUCTS FOR SPECIFIC COLLECTION
  const handleOpenCollection = async (col: BrandCollection) => {
    setSelectedCollection(col);
    setLoadingProducts(true);
    try {
      const q = query(collection(db, 'products'), where('collectionId', '==', col.id));
      const snapshot = await getDocs(q);
      setCollectionProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching collection products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  // 🟢 INLINE BRUTALIST LOADER
  if (loading) {
    return (
      <div className="flex-1 bg-white flex flex-col items-center justify-center min-h-screen text-black">
        <Loader2 size={64} strokeWidth={3} className="animate-spin mb-6" />
        <h2 className="text-4xl font-black uppercase italic tracking-tighter">Accessing Vault</h2>
        <p className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-[0.4em] mt-2">Retrieving Lookbooks...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white text-black min-h-screen">

      {/* =========================================================================
          📱 MOBILE VIEW: "Desktop Only" Lock Screen
      ========================================================================= */}
      <div className="lg:hidden flex flex-col items-center justify-center min-h-[80vh] p-8 text-center bg-white">
         <Monitor size={48} strokeWidth={1.5} className="mb-8 text-black" />
         <h1 className="text-6xl font-black italic uppercase tracking-tighter mb-6 leading-none text-black">
           Desktop<br/>Only<span className="text-zinc-400">.</span>
         </h1>
         <p className="text-xs font-mono font-bold text-zinc-500 uppercase tracking-widest leading-relaxed max-w-[250px] border-t-2 border-black pt-6">
           The Brand Archives are highly visual and currently optimized strictly for desktop displays. 
         </p>
      </div>

      {/* =========================================================================
          🖥️ DESKTOP VIEW
      ========================================================================= */}
      <div className="hidden lg:flex flex-col pb-24 w-full bg-white min-h-screen">
        
        {/* 🟢 IF A COLLECTION IS OPEN -> SHOW PRODUCT GRID */}
        {selectedCollection ? (
          <div className="animate-in fade-in slide-in-from-right-8 duration-500 w-full">
            
            {/* DETAIL HEADER */}
            <div className="sticky top-0 z-40 bg-white border-b-2 border-black p-6 flex items-center justify-between">
              <button 
                onClick={() => setSelectedCollection(null)} 
                className="flex items-center gap-3 text-black font-black uppercase tracking-widest text-xs hover:bg-black hover:text-white px-4 py-2 border-2 border-transparent hover:border-black transition-colors"
              >
                <ArrowLeft size={16} strokeWidth={3} /> Return to Archives
              </button>
              <div className="text-right">
                <p className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-[0.3em]">{selectedCollection.season || 'SEASON'} // {selectedCollection.brandName || 'BRAND'}</p>
              </div>
            </div>

            {/* HERO BANNER */}
            <div className="w-full h-[300px] border-b-2 border-black relative overflow-hidden bg-zinc-100 flex items-end p-12">
              <SafeImage 
                src={selectedCollection.imageUrl || selectedCollection.image} 
                alt="Banner" 
                className="absolute inset-0 w-full h-full object-cover grayscale opacity-20" 
                fallbackType="image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
              <div className="relative z-10 w-full flex justify-between items-end">
                <h1 className="text-6xl lg:text-8xl font-black text-black italic uppercase tracking-tighter leading-[0.85]">
                  {selectedCollection.title || selectedCollection.collectionName || 'COLLECTION'}
                </h1>
                <div className="bg-black text-white px-6 py-3 font-mono font-bold text-xs uppercase tracking-widest">
                  {collectionProducts.length} Pieces
                </div>
              </div>
            </div>

            {/* PRODUCTS GRID */}
            <div className="w-full p-12">
              {loadingProducts ? (
                <div className="flex flex-col items-center justify-center py-32 text-black">
                  <Loader2 size={48} strokeWidth={3} className="animate-spin mb-4" />
                  <p className="text-xs font-mono font-bold uppercase tracking-[0.3em]">Decrypting Items...</p>
                </div>
              ) : collectionProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-zinc-300 bg-zinc-50">
                  <Fingerprint size={48} strokeWidth={1.5} className="text-zinc-300 mb-6" />
                  <h2 className="text-3xl font-black uppercase tracking-widest text-zinc-400">Classified / Empty</h2>
                  <p className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-[0.2em] mt-2">No active items linked to this collection.</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-6">
                  {collectionProducts.map((product) => (
                    <div 
                      key={product.id} 
                      // 🟢 LOGIC UPDATE: Force the click, and log if the parent forgot to pass the function
                      onClick={() => {
                          if (onSelectProduct) {
                              onSelectProduct(product);
                          } else {
                              console.error("CollectionsView: Clicked a product, but 'onSelectProduct' was not passed down from the parent component!");
                          }
                      }}
                      className="group cursor-pointer border-2 border-black bg-white flex flex-col hover:-translate-y-2 transition-transform duration-300 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                    >
                      <div className="aspect-[3/4] border-b-2 border-black overflow-hidden relative bg-zinc-100 pointer-events-none">
                        <SafeImage 
                          src={product.image || product.imageUrl || (product.images && product.images[0])} 
                          alt={product.name}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                          fallbackType="solid"
                        />
                        <div className="absolute top-4 right-4 bg-white text-black border-2 border-black px-3 py-1 text-[10px] font-black uppercase tracking-widest z-10">
                           View Item
                        </div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col justify-between pointer-events-none">
                        <div>
                            <p className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1">{product.brand}</p>
                            <h3 className="text-black font-black uppercase text-lg leading-tight mb-4 line-clamp-2">{product.name}</h3>
                        </div>
                        <p className="text-black font-mono font-bold text-sm uppercase">{formatCurrency(product.price)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        ) : (

        /* 🟢 MAIN COLLECTIONS GRID (When nothing is selected) */
        <div className="animate-in fade-in duration-500 w-full">
          {/* EDITORIAL HEADER */}
          <div className="w-full border-b-2 border-black pt-12 pb-8 px-12 flex items-end justify-between gap-6 bg-white">
            <div>
              <h1 className="text-8xl font-black text-black italic uppercase tracking-tighter leading-[0.8]">
                Lookbooks<span className="text-zinc-400">.</span>
              </h1>
              <p className="mt-6 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-[0.4em]">
                Official Brand Collections & Seasonal Drops
              </p>
            </div>
            
            <div className="flex border-2 border-black p-1 bg-white">
                <button className="bg-black text-white p-2"><LayoutGrid size={18} /></button>
                <button className="text-zinc-400 p-2 hover:text-black transition-colors"><List size={18} /></button>
            </div>
          </div>

          {/* EMPTY STATE */}
          {collections.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-32 border-b-2 border-black bg-zinc-50 w-full">
                 <Fingerprint size={48} strokeWidth={1.5} className="text-zinc-300 mb-6" />
                 <h2 className="text-3xl font-black uppercase tracking-widest text-zinc-400">Awaiting Signal</h2>
                 <p className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-[0.2em] mt-2">No collections found in database.</p>
             </div>
          ) : (
            /* THE VAULT GRID */
            <div className="grid grid-cols-2 w-full bg-white">
              {collections.map((col, i) => (
                <div 
                  key={col.id} 
                  onClick={() => handleOpenCollection(col)}
                  className={`group relative h-[500px] border-black overflow-hidden cursor-pointer flex flex-col justify-end p-12
                    ${i % 2 === 0 ? 'border-r-2' : ''} border-b-2 bg-white`}
                >
                  {/* Background Image */}
                  <div className="absolute inset-0 z-0 bg-white">
                    <SafeImage 
                        src={col.imageUrl || col.image} 
                        alt={col.title || col.collectionName || 'Collection'}
                        className="w-full h-full object-cover grayscale opacity-40 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                        fallbackType="image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent" />
                  </div>

                  {/* Labeling */}
                  <div className="relative z-10 w-full">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-500 font-mono font-bold text-[10px] uppercase tracking-[0.3em]">
                        {col.season || 'SEASON'} // {col.pieceCount || 0} PIECES
                      </span>
                      
                      {/* Status Badge */}
                      <div className={`flex items-center gap-2 font-mono font-bold text-[10px] uppercase border-2 px-2 py-1 bg-white ${
                        col.status === 'ACTIVE' ? 'border-green-500 text-green-600' : 
                        col.status === 'UPCOMING' ? 'border-orange-500 text-orange-600' : 
                        'border-black text-black'
                      }`}>
                        {col.status === 'UPCOMING' ? <Calendar size={10} strokeWidth={2.5} /> : <div className={`w-2 h-2 rounded-full ${col.status === 'ACTIVE' ? 'bg-green-500 animate-pulse' : 'bg-black'}`} />} 
                        {col.status || 'ARCHIVED'}
                      </div>
                    </div>

                    {/* Brand & Collection Title */}
                    <h3 className="text-xl font-bold text-zinc-400 uppercase tracking-widest mb-1">
                      {col.brandName || 'BRAND'}
                    </h3>
                    <h2 className="text-5xl lg:text-6xl font-black text-black italic uppercase tracking-tighter transition-all leading-none">
                      {col.title || col.collectionName || 'COLLECTION'}
                    </h2>

                    <div className="flex items-end justify-between mt-6">
                      <div className="w-12 h-12 bg-black text-white flex items-center justify-center translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 shadow-xl">
                        <ArrowUpRight size={24} />
                      </div>
                    </div>
                  </div>

                  {/* Editorial Corner Number */}
                  <div className="absolute top-8 right-8 text-[60px] font-black text-zinc-100 leading-none pointer-events-none group-hover:text-zinc-200 transition-colors">
                      0{i + 1}
                  </div>
                </div>
              ))}
              
              {/* Directory Slot */}
              <div className="group relative h-[500px] border-b-2 border-black flex flex-col items-center justify-center p-12 cursor-pointer bg-zinc-50 hover:bg-zinc-100 transition-colors">
                  <div className="w-16 h-16 border-2 border-dashed border-zinc-300 flex items-center justify-center group-hover:border-black transition-colors bg-white">
                      <Fingerprint className="text-zinc-400 group-hover:text-black transition-colors" />
                  </div>
                  <p className="mt-6 text-[10px] font-mono font-bold text-zinc-400 uppercase tracking-[0.3em] group-hover:text-black transition-colors">
                      View Brand Directory
                  </p>
              </div>
            </div>
          )}
        </div>
        )}

      </div>
    </div>
  );
};